import { Alert, Box, Button, Container } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { useContractsByEmployee } from '../hooks/useContractsByEmployee';
import type { ContractListItem } from '../contractTypes';
import type { TableColumn } from '../../../components/ui/tableTemplate/TableTemplateTypes';
import { TableTemplate } from '../../../components/ui/tableTemplate/TableTemplate';
import { renderCell } from '../../../components/ui/tableTemplate/cellRenderers';
import { boldIfSelected } from '../utils/boldRow';
import { resolveLabeledCode } from '../../../lib/translateCode';
import { ContractStatusBadge } from '../../employees/components/ContractStatusBadge';
import { resolveContractStatus } from '../../employees/utils/contractStatus';
import theme from '../../../theme/theme';

type ContractListProps = {
  employeeId: string;
  onSelect: (id: string) => void;
  /**
   * `hc_uuid_key` of the contract whose details are displayed below the list.
   * Drives which row is emphasised in bold — the selected row, not the current
   * contract. `null` when no contract is displayed.
   */
  selectedContractId?: string | null;
  lang: string;
  toolbar?: ReactNode;
  /** Installation "Contract activation" option (`global.contratclosing='O'`). */
  activationOptionEnabled: boolean;
  /** Employee activation flag (`statutactive='O'`). */
  isActivated: boolean;
};

/**
 * Number of history rows shown without scrolling. The section height is calibrated
 * to exactly this many rows (see the measured `maxHeight` below); older rows stay
 * reachable via the section's internal scroll.
 */
const VISIBLE_ROWS = 3;

function getColumns(
  t: TFunction,
  activationOptionEnabled: boolean,
  isActivated: boolean,
  selectedContractId: string | null,
): TableColumn<ContractListItem>[] {
  const boldSx = (row: ContractListItem) => boldIfSelected(row, selectedContractId);
  return [
    {
      key: 'is_current',
      labelKey: 'contract:fields.contractCurrent',
      // Wider than a plain Yes/No: the activated/not-activated badge label is long.
      width: 200,
      // Same current-contract status badge (labels + tags) as the employee list.
      render: row => (
        <ContractStatusBadge
          status={resolveContractStatus({
            activationOptionEnabled,
            hasCurrentContract: row.is_current?.code === 'O',
            isActivated,
          })}
        />
      ),
    },
    {
      key: 'contract_creation_type',
      labelKey: 'contract:fields.contractCreationType',
      width: 140,
      render: row => renderCell('text', resolveLabeledCode(t, 'employeeTables.filterOptions.contractCreationType', row.contract_creation_type) || '-', { t }, boldSx(row)),
    },
    {
      key: 'contract_type',
      labelKey: 'contract:fields.contractType',
      render: row => renderCell('text', resolveLabeledCode(t, 'employeeTables.filterOptions.contractType', row.contract_type) || '-', { t }, boldSx(row)),
    },
    {
      key: 'contract_start_date',
      labelKey: 'contract:fields.contractStartDate',
      width: 130,
      render: row => renderCell('date', row.contract_start_date ?? '-', { t }, boldSx(row)),
    },
    {
      key: 'contract_end_date',
      labelKey: 'contract:fields.contractEndDate',
      width: 130,
      render: row => renderCell('date', row.contract_end_date ?? '-', { t }, boldSx(row)),
    },
    {
      key: 'job_function',
      labelKey: 'contract:fields.jobFunction',
      render: row => renderCell('text', resolveLabeledCode(t, 'employeeTables.filterOptions.jobFunction', row.job_function) || '-', { t }, boldSx(row)),
    },
    {
      key: 'initial_start_date',
      labelKey: 'contract:fields.initialStartDate',
      width: 130,
      render: row => renderCell('date', row.initial_start_date ?? '-', { t }, boldSx(row)),
    },
    {
      key: 'origin_field_contrat',
      labelKey: 'contract:fields.originField',
      width: 130,
      render: row => renderCell('text', row.origin_field_contrat ?? '-', { t }, boldSx(row)),
    },
  ];
}

export function ContractList({
  employeeId,
  onSelect,
  selectedContractId = null,
  lang,
  toolbar,
  activationOptionEnabled,
  isActivated,
}: ContractListProps) {
  const { t } = useTranslation(['contract', 'common']);
  const { data, loading, error, retry } = useContractsByEmployee(employeeId, lang);

  // Height calibrated to VISIBLE_ROWS. `maxHeight` on TableTemplate caps the whole
  // card (toolbar bar + header + rows), so we measure the real distance from the
  // card top down to the bottom of the VISIBLE_ROWS-th data row (scrollTop is 0 on
  // measure, so getBoundingClientRect gives true layout coords even when clipped).
  // Robust to theme/i18n/zoom — no magic row height. Falls back to the theme token
  // until the first measure.
  const tableWrapRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<number>(theme.detailPage.historyTableMaxHeight);

  useLayoutEffect(() => {
    const el = tableWrapRef.current;
    // Only measure when the list actually overflows VISIBLE_ROWS — otherwise the
    // capped height is never applied (maxHeight is only passed when hasScroll), so
    // the measurement would be wasted work.
    if (!el || data.length <= VISIBLE_ROWS) return;
    const measure = () => {
      const table = el.querySelector('table');
      const scroller = table?.parentElement; // TableTemplate's overflow:auto scroll box
      const thead = el.querySelector('thead');
      const rows = el.querySelectorAll('tbody tr');
      if (!table || !scroller || !thead || rows.length === 0) return;
      // Everything above the scroll box (toolbar bar, progress). Both tops are outer
      // container edges → unaffected by the scroller's own scrollTop.
      const overhead = scroller.getBoundingClientRect().top - el.getBoundingClientRect().top;
      const headH = thead.getBoundingClientRect().height;
      let rowsH = 0;
      const n = Math.min(rows.length, VISIBLE_ROWS);
      for (let i = 0; i < n; i++) rowsH += (rows[i] as HTMLElement).offsetHeight;
      const height = Math.ceil(overhead + headH + rowsH) + 1; // +1 so the last row's border isn't clipped
      if (rowsH > 0) setMaxHeight(height);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
    // activationOptionEnabled/isActivated affect the status-badge label length →
    // row height; re-measure when they change (the capped wrapper won't trip the RO).
  }, [data, lang, activationOptionEnabled, isActivated]);

  const hasScroll = data.length > VISIBLE_ROWS;

  const errorAlert = error ? (
    <Box sx={{ p: 2 }}>
      <Alert
        severity="error"
        action={
          <Button size="small" onClick={retry}>
            {t('errors.retry')}
          </Button>
        }
      >
        {t('errors.loadError')}
      </Alert>
    </Box>
  ) : undefined;

  const hasContracts = data.length > 0;

  if (!loading && !error && !hasContracts) return null;

  return (
    <Container maxWidth="xl" sx={{ px: 3, pt: 3, pb: 4 }}>
      <Box ref={tableWrapRef}>
        <TableTemplate<ContractListItem>
          columns={getColumns(t, activationOptionEnabled, isActivated, selectedContractId)}
          rows={data}
          loading={loading}
          toolbar={toolbar}
          errorState={errorAlert}
          stickyHeader
          // ≤ VISIBLE_ROWS: no cap → the section grows to content, guaranteeing all
          // rows are shown with no scroll (AC). Only cap (and thus scroll) beyond that.
          maxHeight={hasScroll ? maxHeight : undefined}
          onRowClick={row => onSelect(row.hc_uuid_key)}
          // Zebra striping + neutral hover, same as the other tables (no blue
          // selected-row tint).
          striped
        />
      </Box>
    </Container>
  );
}
